import MiniSearch from "minisearch";
import { SEARCH_FAQ } from "./searchFaq";

// Words that carry no meaning for search ("what are the …", "how do I …") plus
// words on almost every page of the site, which would otherwise match everything.
const STOP_WORDS = new Set(`
  a an the is are was were be been being am do does did done i im ive me my mine we our
  you your yours it its it's they them their he she his her us
  what whats when whens where wheres which who whos whom why how hows
  can could would should will shall may might must there here this that these those
  of in on at to for from by with about into onto over under up out off as than then
  and or but if so not no nor any some all just also very too
  please tell show find know want need get got give see look looking let lets
  info information details detail thing things anything something
  much many current currently latest today now next upcoming soon
  rsaa redruth association angling
`.trim().split(/\s+/));

// Joined/abbreviated forms people type, rewritten before splitting into words
const PHRASES = [
  [/\bc\s*&\s*r\b|\bc and r\b/g, "catch release"],
  [/\bweigh-?ins?\b/g, "weigh"],
  [/\bweighins?\b/g, "weigh"],
  [/\bcomps?\b/g, "competition"],
  [/\bf\.?o\.?t\.?m\b/g, "fish month"],
  [/\bw3w\b/g, "what3words"],
];

// Light stemmer so "rules"/"rule", "weighing"/"weigh", "winners"/"winner" match.
// It only has to be consistent (index and query go through the same function).
export function stem(word) {
  let w = word.toLowerCase().replace(/'s$/, "").replace(/'/g, "");
  if (w.length <= 3 || /\d/.test(w)) return w;
  if (w.endsWith("ies") && w.length > 4) return w.slice(0, -3) + "y";
  if (w.endsWith("ing") && w.length > 5) {
    w = w.slice(0, -3);
    return /([^aeiouls])\1$/.test(w) ? w.slice(0, -1) : w; // "netting" → "net"
  }
  if (w.endsWith("ed") && w.length > 4 && !w.endsWith("eed")) {
    w = w.slice(0, -2);
    return /([^aeiouls])\1$/.test(w) ? w.slice(0, -1) : w;
  }
  if (/(sses|xes|ches|shes)$/.test(w)) return w.slice(0, -2);
  if (w.endsWith("s") && !w.endsWith("ss") && !w.endsWith("us")) return w.slice(0, -1);
  return w;
}

// Extra words to look for, keyed by stem. Matches on these count for less than
// the words actually typed, so they widen the net without swamping results.
const SYNONYMS = {
  winner: ["award", "trophy", "champion", "leaderboard", "result"],
  won: ["winner", "award", "trophy"],
  result: ["winner", "leaderboard", "award"],
  leaderboard: ["winner", "result", "standing"],
  prize: ["award", "trophy"],
  rule: ["regulation"],
  regulation: ["rule"],
  fixture: ["calendar", "competition", "event"],
  calendar: ["fixture"],
  event: ["fixture", "competition"],
  match: ["competition"],
  weigh: ["weight", "scale", "measure"],
  weight: ["weigh", "scale"],
  scale: ["weigh"],
  measure: ["weigh", "length"],
  join: ["membership", "member"],
  sign: ["membership", "join"],
  cost: ["fee", "price", "membership"],
  price: ["fee", "cost"],
  fee: ["cost", "price"],
  pay: ["payment", "sumup"],
  meeting: ["agenda", "minute"],
  meet: ["agenda", "minute"],
  minute: ["meeting"],
  agenda: ["meeting"],
  contact: ["email", "phone", "whatsapp"],
  email: ["contact"],
  phone: ["contact"],
  secretary: ["officer"],
  chairman: ["officer"],
  committee: ["officer"],
  officer: ["committee"],
  weather: ["condition", "forecast", "wind"],
  mark: ["venue", "spot", "beach"],
  venue: ["mark", "beach"],
  spot: ["mark", "venue"],
  shop: ["tackle", "bait"],
  bait: ["tackle", "shop"],
  junior: ["youth", "child", "kid"],
  kid: ["junior", "youth"],
  child: ["junior", "youth"],
  safety: ["safe", "risk", "what3words"],
  specimen: ["cfsa", "percentage"],
  percentage: ["specimen", "cfsa"],
  record: ["best"],
  webcam: ["camera", "view"],
};

// Turn a typed question into the meaningful search words
export function extractKeywords(query) {
  let q = String(query || "").toLowerCase();
  for (const [re, replacement] of PHRASES) q = q.replace(re, replacement);
  const words = q.split(/[^a-z0-9£'&]+/).map((w) => w.replace(/^'+|'+$/g, "")).filter(Boolean);
  const kept = words.filter((w) => !STOP_WORDS.has(w) && (w.length > 1 || /\d/.test(w)));
  return [...new Set(kept.map(stem))];
}

function expandWithSynonyms(keywords) {
  const extra = new Set();
  for (const k of keywords) (SYNONYMS[k] || []).forEach((s) => extra.add(stem(s)));
  keywords.forEach((k) => extra.delete(k));
  return [...extra];
}

const tokenize = (text) => String(text).split(/[^\p{L}\p{N}£&]+/u);

// Indexing uses the same stop words and stemmer as queries, so they line up
const processTerm = (term) => {
  const t = term.toLowerCase();
  return STOP_WORDS.has(t) ? null : stem(t);
};

export function buildSearch(docs) {
  const faqDocs = SEARCH_FAQ.map((f, i) => ({
    id: `faq:${i}`,
    kind: "faq",
    title: f.question,
    keywords: f.keywords,
    text: f.answer,
    faqIndex: i,
  }));

  const mini = new MiniSearch({
    fields: ["title", "keywords", "text"],
    storeFields: ["kind", "title", "url", "page", "text", "faqIndex"],
    tokenize,
    processTerm,
  });
  mini.addAll([...faqDocs, ...docs]);
  return mini;
}

// Typo tolerance: 1 letter wrong for 5–8 letter words ("riules"), 2 for longer
const fuzzyFor = (term) => (term.length >= 9 ? 2 : term.length >= 5 ? 1 : 0);

/**
 * Search the site. Returns { keywords, faq: [...], results: [...] } where results
 * are one per page or PDF, each with its best-matching passage.
 */
export function runSearch(mini, query, { limit = 8 } = {}) {
  const keywords = extractKeywords(query);
  if (!keywords.length) return { keywords, faq: [], results: [] };

  const synonyms = expandWithSynonyms(keywords);
  const synonymSet = new Set(synonyms);
  const lastTyped = keywords[keywords.length - 1];

  const hits = mini.search([...keywords, ...synonyms].join(" "), {
    // Terms are already stemmed and stop-worded by extractKeywords
    tokenize: (q) => q.split(" "),
    processTerm: (t) => t,
    combineWith: "OR",
    boost: { title: 3, keywords: 3, text: 1 },
    boostTerm: (term) => (synonymSet.has(term) ? 0.35 : 1),
    fuzzy: (term) => fuzzyFor(term) / Math.max(term.length, 1),
    prefix: (term) => term === lastTyped && term.length >= 3,
    boostDocument: (id, term, stored) => (stored?.kind === "faq" ? 2.5 : 1),
  });

  // Prefer passages that match more of the typed words, not just one of them
  const scored = hits
    .map((h) => {
      const matched = keywords.filter((k) => h.queryTerms.includes(k)).length;
      return { ...h, score: h.score * (0.5 + matched / keywords.length) };
    })
    .sort((a, b) => b.score - a.score);

  const top = scored[0]?.score || 0;
  const faq = scored
    .filter((h) => h.kind === "faq" && h.score >= top * 0.45)
    .slice(0, 2)
    .map((h) => ({ ...SEARCH_FAQ[h.faqIndex], score: h.score }));
  const faqUrls = new Set(faq.flatMap((f) => f.links.map((l) => l.url)));

  const seen = new Set();
  const results = [];
  for (const h of scored) {
    if (h.kind === "faq") continue;
    // One result per page or PDF (its best page), and don't repeat what the answer card links to
    const key = h.url.split("#")[0];
    if (seen.has(key) || faqUrls.has(key)) continue;
    seen.add(key);
    results.push({
      id: h.id,
      kind: h.kind,
      title: h.title,
      url: h.url,
      page: h.page,
      snippet: makeSnippet(h.text, h.terms),
      score: h.score,
    });
    if (results.length >= limit) break;
  }
  return { keywords, faq, results };
}

// A ~30-word window of the passage around the first matched word, split into
// parts so the UI can highlight matches without using innerHTML
export function makeSnippet(text, matchedTerms, windowWords = 30) {
  const words = String(text).split(" ");
  const terms = new Set(matchedTerms);
  const isMatch = (w) => {
    const t = w.toLowerCase().replace(/[^\p{L}\p{N}£&']/gu, "");
    return Boolean(t) && !STOP_WORDS.has(t) && terms.has(stem(t));
  };
  const first = Math.max(0, words.findIndex(isMatch));
  const start = Math.max(0, Math.min(first - 8, words.length - windowWords));
  const parts = words.slice(start, start + windowWords).map((w) => ({ text: w, match: isMatch(w) }));
  return { before: start > 0, after: start + windowWords < words.length, parts };
}

// "When is the next comp?" is answered from the fixtures list rather than text search
const NEXT_WORDS = /\b(next|upcoming|coming up|soon)\b/;
const FIXTURE_WORDS = /\b(comps?|competitions?|fixtures?|match(es)?|events?|trips?|fishing|weigh)\b/;

export function wantsNextFixture(query) {
  const q = String(query || "").toLowerCase();
  return NEXT_WORDS.test(q) && FIXTURE_WORDS.test(q);
}

export function nextFixtures(fixtures, now = new Date()) {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const next = fixtures.find((f) => f.isoDate && new Date(f.isoDate) >= today);
  // Month-long competitions that started this calendar month are still running
  const running = fixtures.filter((f) => {
    if (!f.monthLong || !f.isoDate) return false;
    const start = new Date(f.isoDate);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    return start <= today && today <= end;
  });
  return { next, running };
}
